import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { bolAPI } from '../utils/api';
import { formatDecimalOrNull, toNumber } from '../utils/format';
import type { BOL, FreightItem } from '../types';

const totalWeight = (freight: FreightItem[]): string | null =>
  formatDecimalOrNull(freight.reduce((sum, f) => sum + toNumber(f.weight), 0));

const BOLPage: React.FC = () => {
  const [bols, setBols] = useState<BOL[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchBOLs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await bolAPI.getBOLs({
        search: { value: searchTerm },
      });
      
      setBols(response.data || []);
    } catch (error) {
      console.error('Error fetching BOLs:', error);
      toast.error('Failed to load BOLs');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchBOLs();
  }, [fetchBOLs]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const getStatusBadge = (status: string) => {
    const statusStyles: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    
    const statusLabels: Record<string, string> = {
      pending: 'Pending',
      in_progress: 'In Progress',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <h1 className="text-xl font-bold text-gray-900">Bills of Lading</h1>
        <button
          onClick={fetchBOLs}
          disabled={loading}
          className="p-1.5 rounded-md text-gray-500 hover:text-blue-600 hover:bg-blue-50 active:bg-blue-100 transition-colors disabled:opacity-40"
          title="Refresh"
        >
          <svg
            className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-3">
        <input
          type="text"
          placeholder="Search BOLs..."
          value={searchTerm}
          onChange={handleSearch}
          className="w-full md:w-64 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* BOL Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : bols.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-gray-500">
            <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-base font-medium">No BOLs found</p>
            <p className="text-xs">You don't have any bills of lading assigned yet.</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200">
              {bols.map((bol, index) => {
                const showManifestHeader = bol.loadID && (index === 0 || bols[index - 1].loadID !== bol.loadID);
                
                return (
                  <React.Fragment key={bol.id}>
                    {/* Manifest Header */}
                    {showManifestHeader && (
                      <div className="bg-blue-50 px-3 py-2 border-b border-blue-200">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="text-sm font-semibold text-blue-900">
                            Manifest: {bol.loadName || `Load #${bol.loadID}`}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    <Link
                      to={`/bol/${bol.id}`}
                      className="block bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors"
                    >
                      {/* Row 1: BOL# + Status, Customer, Name → Name */}
                      <div className="p-3 pb-1">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            {bol.loadID && (
                              <span className="text-xs font-medium text-blue-600">#{bol.loadIndex}</span>
                            )}
                            <span className="text-xs font-semibold text-gray-900">{bol.bolNumber || bol.idbol || 'N/A'}</span>
                          </div>
                          {getStatusBadge(bol.status)}
                        </div>
                        {bol.customerName && (
                          <div className="text-xs font-semibold text-indigo-600 truncate mb-0.5">{bol.customerName}</div>
                        )}
                        <div className="text-xs text-gray-900 font-medium truncate">
                          {bol.PickName} → {bol.DropName}
                        </div>
                      </div>

                      {/* Row 2: Customer Ref, MAWB, HAWB */}
                      <div className="px-3 pb-1">
                        <div className="flex items-center gap-3 text-xs text-gray-600">
                          {bol.freight && bol.freight.length > 0 && bol.freight[0].customerRef && (
                            <span className="truncate">Ref: {bol.freight[0].customerRef}</span>
                          )}
                          {bol.freight && bol.freight.length > 0 && bol.freight[0].mawb && (
                            <span className="truncate">MAWB: {bol.freight[0].mawb}</span>
                          )}
                          {bol.freight && bol.freight.length > 0 && bol.freight[0].hawb && (
                            <span className="truncate">HAWB: {bol.freight[0].hawb}</span>
                          )}
                        </div>
                      </div>

                      {/* Row 3: Pcs and Wt */}
                      <div className="px-3 pb-3">
                        <div className="flex items-center gap-4 text-xs text-gray-600">
                          {bol.freight && bol.freight.length > 0 && (
                            <>
                              <span>Pcs: {bol.freight.reduce((sum, f) => sum + toNumber(f.pieces), 0)}</span>
                              <span>Wt: {totalWeight(bol.freight) ? `${totalWeight(bol.freight)} lbs` : 'N/A'}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </Link>
                  </React.Fragment>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BOLPage; 